import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Loader2, MessageSquarePlus, ShieldCheck, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { getLimitedPublishChanges, publishLimited } from '@/api/site-config';
import { canPublishLimited, type LimitedAccess } from '@/lib/limitedAccess';

/**
 * The bar a limited person sees above every page (Dawn, 2026-11-02): who changes the design, a
 * preview of their draft, and "Publish my changes" — Limited Publish, which puts live only what
 * they may change (agents, offices, teams, blog, communities, profile, business details) and
 * leaves any design work the Bread List team has in the draft unpublished.
 */
export function LimitedSiteBar({ access }: { access: LimitedAccess }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const publishes = canPublishLimited(access);

  const { data: changes = [], isLoading } = useQuery({
    queryKey: ['limited-publish-changes'],
    queryFn: getLimitedPublishChanges,
    enabled: publishes,
    staleTime: 10_000,
    refetchInterval: 30_000,
    retry: false,
  });

  const publish = useMutation({
    mutationFn: publishLimited,
    onSuccess: (res) => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['limited-publish-changes'] });
      void queryClient.invalidateQueries({ queryKey: ['site-config'] });
      toast({ title: 'Published', description: `${res.published.map((c) => c.label).join(', ')} ${res.published.length === 1 ? 'is' : 'are'} live on your site.` });
    },
    onError: (err: unknown) => {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast({ title: 'Could not publish', description: message ?? 'Please try again in a moment.', variant: 'destructive' });
    },
  });

  const who = access.managed
    ? 'Managed by Bread List. Our team changes the design, pages, menu, logo, colors and domain.'
    : 'You have team access to part of this site.';

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-brand-border bg-brand-lilac-soft px-4 py-2 text-sm" data-limited-bar>
      <div className="flex min-w-0 items-center gap-2 text-brand-ink">
        <ShieldCheck className="h-4 w-4 shrink-0 text-brand-plum" />
        <span className="min-w-0">{who}</span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button asChild size="sm" variant="ghost" className="text-brand-plum">
          <Link to="/studio?view=preview">
            <Eye className="mr-1.5 h-4 w-4" />
            Preview my site
          </Link>
        </Button>
        {access.managed && access.areas.includes('requests') && (
          <Button asChild size="sm" variant="ghost" className="text-brand-plum">
            <Link to="/requests" data-limited-request>
              <MessageSquarePlus className="mr-1.5 h-4 w-4" />
              Request a change
            </Link>
          </Button>
        )}
        {publishes && (
          <Button
            size="sm"
            className="bg-brand-plum text-white hover:bg-brand-plum-deep"
            disabled={isLoading || changes.length === 0}
            onClick={() => setOpen(true)}
            title={changes.length === 0 ? 'Nothing waiting to publish' : undefined}
            data-limited-publish
          >
            <Upload className="mr-1.5 h-4 w-4" />
            {changes.length === 0 ? 'Nothing to publish' : changes.length === 1 ? 'Publish 1 change' : `Publish ${changes.length} changes`}
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Publish your changes?</DialogTitle>
            <DialogDescription>These go live on your site now:</DialogDescription>
          </DialogHeader>
          <ul className="list-disc space-y-1 pl-5 text-sm text-brand-ink" data-limited-publish-list>
            {changes.map((c) => <li key={c.key}>{c.label}</li>)}
          </ul>
          {access.managed && (
            <p className="text-xs text-brand-ink-soft">
              Any design changes the Bread List team is still working on stay unpublished.
            </p>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={publish.isPending}>Cancel</Button>
            <Button className="bg-brand-plum text-white hover:bg-brand-plum-deep" disabled={publish.isPending} onClick={() => publish.mutate()}>
              {publish.isPending ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Publishing…</> : 'Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
